import { Component, type ComponentChildren } from "preact";

interface LazyBoundaryProps {
  children: ComponentChildren;
  /** Rendered if the lazy subtree throws (e.g. its chunk fails to download). */
  fallback?: ComponentChildren;
  /** Label for the console/Sentry breadcrumb. */
  label?: string;
}

interface LazyBoundaryState {
  failed: boolean;
}

/**
 * Error boundary for `lazy()` subtrees. When a code-split chunk fails to load —
 * a flaky mobile connection mid-download, or a stale cached index.html pointing
 * at hashed chunks that no longer exist after a redeploy — Preact's Suspense
 * rethrows the rejected promise during render. Without a boundary that throw
 * propagates to the root and unmounts the whole app (white screen). This
 * contains the failure to the lazy subtree so the rest of the surface (the
 * microsite CTAs, or the AR session) keeps working and we degrade to `fallback`.
 *
 * Suspense handles the *pending* state (fallback while loading); this handles
 * the *rejected* state. Use them together around every `lazy()` component.
 */
export class LazyBoundary extends Component<LazyBoundaryProps, LazyBoundaryState> {
  state: LazyBoundaryState = { failed: false };

  static getDerivedStateFromError(): LazyBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    console.error(
      `LazyBoundary${this.props.label ? ` (${this.props.label})` : ""}: lazy subtree failed to load`,
      error,
    );
  }

  render() {
    if (this.state.failed) return this.props.fallback ?? null;
    return this.props.children;
  }
}
